import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Warning, Detective, Prohibit } from "@phosphor-icons/react";

export const ROUTING_CONFIG = {
  auto_approve: { label: "Auto Approve", bg: "bg-green-50", border: "border-green-400", text: "text-green-900", bar: "[&>div]:bg-green-500", icon: ShieldCheck, iconColor: "text-green-600" },
  review: { label: "Adjudicator Review", bg: "bg-yellow-50", border: "border-yellow-400", text: "text-yellow-900", bar: "[&>div]:bg-yellow-500", icon: Warning, iconColor: "text-yellow-600" },
  investigate: { label: "Investigator Review", bg: "bg-orange-50", border: "border-orange-400", text: "text-orange-900", bar: "[&>div]:bg-orange-500", icon: Detective, iconColor: "text-orange-600" },
  reject_recommended: { label: "Hard Hold / Reject", bg: "bg-red-50", border: "border-red-500", text: "text-red-900", bar: "[&>div]:bg-red-600", icon: Prohibit, iconColor: "text-red-600" },
};

export const SEVERITY_STYLE = {
  critical: "bg-red-600 text-white border-0",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  medium: "bg-yellow-100 text-yellow-800 border-yellow-300",
  low: "bg-gray-100 text-gray-700 border-gray-300",
};

export default function FraudScoreBanner({ analysis }) {
  const cfg = ROUTING_CONFIG[analysis.routing_decision] || ROUTING_CONFIG.auto_approve;
  const Icon = cfg.icon;
  const counts = analysis.severity_counts || {};
  const breakdown = Object.entries(analysis.category_breakdown || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className={`border-2 rounded-md p-5 ${cfg.bg} ${cfg.border}`} data-testid="fraud-score-banner">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Icon size={30} weight="fill" className={cfg.iconColor} />
          <div>
            <p className="label-xs opacity-70">Routing Decision</p>
            <p className={`text-lg font-bold ${cfg.text}`} data-testid="fraud-routing-label">{analysis.routing_label || cfg.label}</p>
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              {["critical", "high", "medium", "low"].map(s => (
                <Badge key={s} variant="outline" className={`text-[10px] ${SEVERITY_STYLE[s]}`} data-testid={`sev-count-${s}`}>
                  {counts[s] || 0} {s}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="label-xs opacity-70">Composite Score</p>
          <p className={`text-5xl font-black leading-none ${cfg.text}`} data-testid="fraud-composite-score">{analysis.composite_score ?? 0}</p>
          <p className="text-xs opacity-60">out of 100</p>
        </div>
      </div>
      <Progress value={analysis.composite_score || 0} className={`h-3 mt-4 ${cfg.bar}`} />
      <div className="flex justify-between mt-1.5 text-[10px] text-gray-400 font-medium">
        <span>0 Auto-approve</span><span>25 Review</span><span>50 Investigate</span><span>75 Hard hold</span><span>100</span>
      </div>
      {breakdown.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="fraud-category-breakdown">
          {breakdown.map(([cat, pts]) => (
            <span key={cat} className="text-[11px] px-2 py-1 rounded bg-white/70 border border-gray-200 text-gray-700 capitalize">
              {cat}: <span className="font-bold">{pts} pts</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
